/** @format */
import React, { useCallback, useEffect, useRef, useState } from "react";
import GridLayout from "react-grid-layout";
import type { ImageMetaData } from "src/util/imageMetadata";
import { useMeasure } from "../../../hooks/useMeasure";
import { cn } from "../../../util/classNameConstructor";
import type { Question, SurveyImage } from "../../special/survey/survey.types";
import type { useEditQuestion } from "../../special/survey/utils";
import DraggableImagesItem from "./DraggableImagesItem";

export interface DraggableImagesProps
  extends React.HTMLAttributes<HTMLDivElement> {
  layout: NonNullable<Question["imgUrl"]>;
  updateQuestion: ReturnType<typeof useEditQuestion>["updateQuestion"];
  updateLayout: GridLayout.ReactGridLayoutProps["onLayoutChange"];
  onHeightChange: (height: number) => void;
  rowHeight?: number;
  rowGrid?: number;
  minHeight?: number;
  maxHeight?: number;
  mode: "edit" | "display";
  imageFocus?: number | false;
  classNames?: {
    layout?: string;
  };
  imageMetadata: Record<string, ImageMetaData>;
  handleFileChange?: (files: FileList | File[], uid: string) => Promise<void>;
}

const sameLayouts = (
  gridLayout: GridLayout.Layout[],
  surveyImages: SurveyImage[],
) => {
  const getRaw = ({ i, x, y, w, h }) => ({ i, x, y, w, h });
  return (
    JSON.stringify(gridLayout.map(getRaw)) ===
    JSON.stringify(surveyImages.map(getRaw))
  );
};

const DraggableImages = ({
  project,
  updateLayout,
  rowHeight,
  updateQuestion,
  layout,
  className,
  classNames,
  onHeightChange,
  rowGrid = 5,
  handleFileChange,
  ...props
}: DraggableImagesProps) => {
  const [settings, setSettings] = React.useState({
    rowHeight,
    cols: 2,
  });

  const adjustDimensions = useCallback(
    (rect: DOMRectReadOnly, grid: number) => {
      setSettings(({ cols }) => ({
        rowHeight: Math.floor(rect.width / cols / grid),
        cols,
      }));
    },
    [],
  );

  const [{ ref }, { width = 400 }] = useMeasure<HTMLDivElement, [number]>(
    adjustDimensions,
    rowGrid,
  );
  const [{ ref: gridRef }, { height }] = useMeasure<HTMLDivElement, [number]>(
    adjustDimensions,
    rowGrid,
  );

  const removeImage = useCallback(
    (i: string) => {
      const l = layout?.slice()?.filter((e) => e.i !== i);
      if (!l) return;
      l.forEach((e) => {
        delete e.minH;
        delete e.maxH;
      });
      updateQuestion(l, "imgUrl");
    },
    [layout],
  );
  const timeoutId = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (height !== undefined) {
      clearTimeout(timeoutId.current);
      timeoutId.current = setTimeout(() => {
        onHeightChange(height);
      }, 100);
    }
  }, [height, onHeightChange]);

  const [animatedClass, setAnimatedClass] = React.useState(
    "duration-300 opacity-0",
  );

  useEffect(() => {
    setTimeout(() => setAnimatedClass("opacity-100"), 500);
  }, []);

  const updateImage = useCallback(
    (i: string, updates: Partial<SurveyImage>) => {
      const newLayout = layout?.map((item) => {
        if (item.i === i) {
          return { ...item, ...updates };
        }
        return item;
      });
      updateQuestion(newLayout, "imgUrl");
    },
    [layout, updateQuestion],
  );

  return (
    <div ref={ref} className={cn(className)}>
      {layout && width > 10 && (
        <GridLayout
          innerRef={gridRef}
          cols={settings.cols}
          rowHeight={settings.rowHeight}
          width={width}
          onLayoutChange={(e) => {
            if (!sameLayouts(e, layout)) updateLayout?.(e);
          }}
          margin={[0, 0]}
          style={{ userSelect: "none" }}
          isDraggable={props.mode === "edit"}
          isResizable={props.mode === "edit"}
          resizeHandles={["se"]}
          layout={layout}
          autoSize
          className={cn("duration-300", animatedClass, classNames?.layout)}
        >
          {layout.map((imageProps, index) => {
            return (
              <DraggableImagesItem
                project={project}
                {...imageProps}
                {...props}
                key={imageProps.i}
                index={index}
                removeImage={removeImage}
                updateImage={updateImage}
                handleFileChange={handleFileChange}
              />
            );
          })}
        </GridLayout>
      )}
    </div>
  );
};

export default DraggableImages;
